import { useEffect, useLayoutEffect, type RefObject } from "react";
import gsap from "gsap";

// useLayoutEffect warns / no-ops on the server (TanStack Start SSR), so fall back.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Entrance, floating cards, popup bobbing + progress rings, pulsing screens.
 * Does nothing (illustration stays static and visible) when the user
 * prefers reduced motion.
 */
export function useHeroIllustrationAnimation(scope: RefObject<SVGSVGElement | null>) {
  useIsoLayoutEffect(() => {
    const root = scope.current;
    if (!root) return;

    const mm = gsap.matchMedia();

    mm.add(
      "(prefers-reduced-motion: no-preference)",
      () => {
        const q = gsap.utils.selector(root);

        const intro = gsap.timeline({ defaults: { ease: "power3.out" } });
        intro
          .from(root, { autoAlpha: 0, y: 24, duration: 0.8 })
          .from(q(".student"), { autoAlpha: 0, y: 14, duration: 0.5, stagger: 0.08 }, "-=0.4")
          .from(q(".teacher"), { autoAlpha: 0, x: -24, duration: 0.6 }, "-=0.3")
          .from(
            q(".popup"),
            {
              autoAlpha: 0,
              scale: 0.6,
              transformOrigin: "50% 100%",
              duration: 0.5,
              ease: "back.out(1.7)",
              stagger: 0.1,
            },
            "-=0.2",
          )
          .from(
            q(".float-card"),
            {
              autoAlpha: 0,
              scale: 0.6,
              transformOrigin: "50% 50%",
              duration: 0.6,
              ease: "back.out(1.7)",
              stagger: 0.1,
            },
            "-=0.5",
          );

        // progress rings fill up after the popups appear
        q(".progress-ring").forEach((el, i) => {
          const c = Number(el.getAttribute("data-circumference"));
          const target = Number(el.getAttribute("data-offset"));
          gsap.fromTo(
            el,
            { strokeDashoffset: c },
            { strokeDashoffset: target, duration: 1.2, ease: "power2.out", delay: 1.1 + i * 0.1 },
          );
        });

        q(".float-card").forEach((el, i) => {
          gsap.to(el, {
            y: i % 2 ? 8 : -8,
            duration: 2 + i * 0.35,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
            delay: 1.2 + i * 0.2,
          });
        });

        // popups hover a little less than the big cards
        q(".popup").forEach((el, i) => {
          gsap.to(el, {
            y: -4,
            duration: 1.8 + (i % 3) * 0.3,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
            delay: 1.4 + i * 0.25,
          });
        });

        q(".screen").forEach((el, i) => {
          gsap.to(el, {
            opacity: 0.82,
            duration: 1.6,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
            delay: 1.5 + i * 0.3,
          });
        });

        // small status lights on the student monitors
        q(".led").forEach((el, i) => {
          gsap.to(el, {
            opacity: 0.25,
            duration: 0.9,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
            delay: i * 0.2,
          });
        });
      },
      root,
    );

    return () => mm.revert();
  }, [scope]);
}
